// richiesto da CRA 2 — la JSX transform automatica non è disponibile in react-scripts 2.x
import React, { useEffect, useRef, useState } from 'react'
import { Button, Dimmer, Form, Icon, Loader, Message } from 'semantic-ui-react'
import PropTypes from 'prop-types'
import '../SuperCutoff.css'

// Mappa numero mese (1-12) -> etichetta visibile.
const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/**
 * Step 7 — Settaggio SuperCutoff PM e trigger automatico della closure.
 * Inserisce il record cutoff_pm con is_super_cutoff=1 e pubblica su SNS.
 *
 * Il componente implementa un pattern di conferma a due step:
 * idle → conferma visibile → esecuzione, per proteggere un'azione irreversibile.
 * Quando result è valorizzato, il componente mostra il riepilogo e disabilita l'interazione.
 *
 * Il periodo non è scelto liberamente dall'utente: viene derivato dalla lista cutoff PM
 * caricata dal container e corrisponde al mese successivo all'ultimo super cutoff registrato.
 * Il payload inviato a onExecute è { month, year } preso direttamente da nextPeriod.
 *
 * Props:
 *   result             {object}    [opzionale] { cutoff_date, total_budgets_to_process, execution_id }.
 *                                  Quando valorizzato, il componente entra nello stato "completed".
 *   nextPeriod         {object}    [opzionale] { month, year, date_start, date_end } derivato da cutoff_pm.
 *                                  Se null, lo step e bloccato e mostra un messaggio di errore.
 *   loadingNextPeriod  {boolean}   [opzionale] True mentre la lista cutoff PM e in caricamento. Default: false
 *   onExecute          {Function}  Firma: fn({ month, year })
 *   loading            {boolean}   [opzionale] Default: false
 */
const StepSetSuperCutoff = ({
  result,
  nextPeriod,
  loadingNextPeriod = false,
  onExecute,
  loading = false,
}) => {
  const [confirmVisible, setConfirmVisible] = useState(false)

  // Ref al primo bottone del pannello di conferma per gestione focus programmatica
  const confirmButtonRef = useRef(null)
  // Ref al bottone iniziale per ripristino focus quando il pannello di conferma viene chiuso
  const triggerButtonRef = useRef(null)

  // Sposta il focus sul bottone Confirm quando il pannello di conferma appare,
  // e lo riporta sul bottone iniziale quando il pannello viene chiuso (Cancel).
  // result è in dipendenza per evitare il ripristino del focus quando il componente
  // entra nello stato completed: in quel caso triggerButtonRef non è più nel DOM.
  useEffect(() => {
    if (confirmVisible && confirmButtonRef.current) {
      confirmButtonRef.current.focus()
    } else if (!confirmVisible && !result && triggerButtonRef.current) {
      triggerButtonRef.current.focus()
    }
  }, [confirmVisible, result])

  // Allinea lo stato interno quando result diventa truthy (es. dopo onExecute):
  // garantisce che confirmVisible non resti true se result arriva prima del re-render
  useEffect(() => {
    if (result) {
      setConfirmVisible(false)
    }
  }, [result])

  const hasNextPeriod = nextPeriod !== null && nextPeriod !== undefined
  const month = hasNextPeriod ? nextPeriod.month : null
  const year = hasNextPeriod ? nextPeriod.year : null
  const monthLabel = (month !== null) ? MONTH_LABELS[month - 1] : ''

  // Le opzioni delle select contengono SOLO il valore vincolato (Q2 = a)
  const monthOptions = hasNextPeriod
    ? [{ key: month, value: month, text: monthLabel }]
    : []
  const yearOptions = hasNextPeriod
    ? [{ key: year, value: year, text: String(year) }]
    : []

  const periodReady = hasNextPeriod && !loadingNextPeriod
  const periodBlocked = !loadingNextPeriod && !hasNextPeriod

  const handleExecute = () => {
    // Guard contro doppio submit in caso di re-render in ritardo
    if (loading) return
    if (!hasNextPeriod) return
    setConfirmVisible(false)
    onExecute({ month: nextPeriod.month, year: nextPeriod.year })
  }

  return (
    <div>
      <p className="supercutoff-step-description">
        Sets the SuperCutoff PM record and automatically triggers the closure queue.
        The system will insert the cutoff date and publish a message to the SNS topic,
        which starts the <strong>CutOffSchedulerClosure</strong> process.
        The period is locked to the month following the latest super cutoff.
      </p>

      {/* Loader sulla selezione finche cutoff_pm non e arrivato */}
      {loadingNextPeriod && (
        <div className="supercutoff-period-loader">
          <Dimmer active inverted>
            <Loader inverted content="Loading target period" size="small" />
          </Dimmer>
        </div>
      )}

      {/* Errore bloccante: cutoff_pm caricato ma vuoto/non valido */}
      {periodBlocked && !result && (
        <Message negative icon>
          <Icon name="ban" aria-hidden="true" />
          <Message.Content>
            <Message.Header>Cannot determine target period</Message.Header>
            <p>
              The super cutoff list is unavailable or empty. Reload the page to retry, or
              contact the administrator if the problem persists.
            </p>
          </Message.Content>
        </Message>
      )}

      {/* Fase idle: selezione mese/anno bloccata */}
      {!result && periodReady && (
        <Form className="supercutoff-form-wrapper" aria-label="Selected period (locked)">
          <Form.Group widths="equal">
            <Form.Select
              label="Cutoff Month"
              options={monthOptions}
              value={month}
              disabled
            />
            <Form.Select
              label="Cutoff Year"
              options={yearOptions}
              value={year}
              disabled
            />
          </Form.Group>
        </Form>
      )}

      {/* Fase conferma: avviso visibile e annunciato agli screen reader */}
      {!result && confirmVisible && (
        <div
          className="supercutoff-confirm-box"
          role="alert"
          aria-live="assertive"
          id="supercutoff-confirm-message"
        >
          <Icon name="warning sign" color="yellow" size="large" aria-hidden="true" />
          <span className="confirm-text">
            You are about to set the SuperCutoff for{' '}
            <strong>
              {monthLabel} {year}
            </strong>{' '}
            and trigger the automatic closure queue. This will start processing all pending
            budgets. This action cannot be undone.
          </span>
        </div>
      )}

      {/* Fase completata: messaggio di successo */}
      {result && (
        <Message positive icon>
          <Icon name="check circle" aria-hidden="true" />
          <Message.Content>
            <Message.Header>SuperCutoff set — closure queue triggered</Message.Header>
            <p>
              Cutoff date: <strong>{result.cutoff_date || '—'}</strong>
              <br />
              Budgets to process:{' '}
              <strong>
                {result.total_budgets_to_process != null
                  ? result.total_budgets_to_process
                  : '—'}
              </strong>
              <br />
              Execution ID: <code>{result.execution_id || '—'}</code>
            </p>
          </Message.Content>
        </Message>
      )}

      <div className="supercutoff-step-actions">
        {/* Bottone iniziale — fase idle */}
        {!result && !confirmVisible && (
          <Button
            color="orange"
            icon
            labelPosition="left"
            onClick={() => setConfirmVisible(true)}
            disabled={loading || !periodReady}
            ref={triggerButtonRef}
          >
            <Icon name="play" aria-hidden="true" />
            Set SuperCutoff &amp; trigger closure
          </Button>
        )}

        {/* Bottoni conferma/annulla — fase conferma */}
        {!result && confirmVisible && (
          <>
            <Button
              color="red"
              icon
              labelPosition="left"
              onClick={handleExecute}
              loading={loading}
              disabled={loading || !periodReady}
              aria-describedby="supercutoff-confirm-message"
              ref={confirmButtonRef}
            >
              <Icon name="check" aria-hidden="true" />
              Confirm
            </Button>
            <Button basic onClick={() => setConfirmVisible(false)} disabled={loading}>
              Cancel
            </Button>
          </>
        )}

        {/* Badge completamento — fase completata */}
        {result && (
          <span className="supercutoff-status-badge completed">
            <Icon name="check" aria-hidden="true" />
            Step completed
          </span>
        )}
      </div>
    </div>
  )
}

StepSetSuperCutoff.propTypes = {
  result: PropTypes.shape({
    cutoff_date: PropTypes.string,
    total_budgets_to_process: PropTypes.number,
    execution_id: PropTypes.string,
  }),
  nextPeriod: PropTypes.shape({
    month: PropTypes.number.isRequired,
    year: PropTypes.number.isRequired,
    date_start: PropTypes.string.isRequired,
    date_end: PropTypes.string.isRequired,
  }),
  loadingNextPeriod: PropTypes.bool,
  onExecute: PropTypes.func.isRequired,
  loading: PropTypes.bool,
}

export default StepSetSuperCutoff
